import { useAuth0 } from '@auth0/auth0-react';
import type { CheckTemplateSummary, DraftQuestionInput } from '@my/check-api';
import {
  useCheckTemplate,
  useCheckTemplates,
  useCreateCheckTemplate,
  useDeleteCheckTemplate,
  useDuplicateCheckTemplate,
  usePublishCheckTemplate,
  useUpdateCheckTemplateDraft,
} from '@my/check-api/hooks';
import { ErrorBanner, Loading, PageHeader, showToast } from '@my/ui';
import { Copy, Edit, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { DriverPreview } from './DriverPreview';
import { NewTemplateWizard } from './NewTemplateWizard';
import { TemplateEditor } from './TemplateEditor';
import { STATUS_BADGE, unitLabel } from './lib/templateTypes';

// ────────────────────────────────────────────────────────────────────────────
// Check Templates — live against /check/api/v1/checktemplates.
//
// The four views sit behind one route because the editor holds unsaved state
// that would be lost on navigation. Splitting them into real routes means
// either lifting that state or prompting on unload; not worth it until the
// screens settle.
//
// Open modelling questions are listed at the bottom of lib/templateTypes.ts.
// ────────────────────────────────────────────────────────────────────────────

type View =
  | { kind: 'grid' }
  | { kind: 'new' }
  | { kind: 'editor'; id: string }
  | { kind: 'preview'; id: string; questions: DraftQuestionInput[] };

export function TemplateListPage() {
  const { t } = useTranslation('checks');
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const [view, setView] = useState<View>({ kind: 'grid' });
  const [search, setSearch] = useState('');

  const listQuery = useCheckTemplates(orgId);
  const activeId = view.kind === 'editor' || view.kind === 'preview' ? view.id : null;
  const detailQuery = useCheckTemplate(activeId, orgId);

  const createMutation = useCreateCheckTemplate();
  const updateMutation = useUpdateCheckTemplateDraft();
  const publishMutation = usePublishCheckTemplate();
  const duplicateMutation = useDuplicateCheckTemplate();
  const deleteMutation = useDeleteCheckTemplate();

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    publishMutation.isPending ||
    duplicateMutation.isPending;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (listQuery.data ?? []).filter(
      (x) => !q || `${x.name} ${unitLabel(x.unitType)}`.toLowerCase().includes(q),
    );
  }, [listQuery.data, search]);

  /** Surfaces the BE's message verbatim — for publish it names the questions to restore. */
  const fail = (err: unknown, fallback: string) =>
    showToast({
      type: 'error',
      message: err instanceof Error && err.message ? err.message : fallback,
    });

  // ── Handlers ─────────────────────────────────────────────────────────────

  const create = async (input: Parameters<typeof createMutation.mutateAsync>[0]) => {
    try {
      const id = await createMutation.mutateAsync(input);
      setView({ kind: 'editor', id });
    } catch (err) {
      fail(err, t('templates.toast.createFailed'));
    }
  };

  const saveDraft = async (questions: DraftQuestionInput[], name: string) => {
    if (!orgId || view.kind !== 'editor') return;
    try {
      await updateMutation.mutateAsync({ id: view.id, organisationId: orgId, name, questions });
      showToast({ type: 'success', message: t('templates.toast.draftSaved') });
    } catch (err) {
      fail(err, t('templates.toast.saveFailed'));
    }
  };

  /**
   * Save then publish — two calls, because the BE publishes whatever is
   * already persisted on the draft. Publishing without saving first would
   * freeze a stale version.
   */
  const publish = async (questions: DraftQuestionInput[], name: string) => {
    if (!orgId || view.kind !== 'editor') return;
    try {
      await updateMutation.mutateAsync({ id: view.id, organisationId: orgId, name, questions });
      await publishMutation.mutateAsync({ id: view.id, organisationId: orgId });
      showToast({ type: 'success', message: t('templates.toast.published', { name }) });
      setView({ kind: 'grid' });
    } catch (err) {
      // A WAC template missing a mandatory question fails here, and the
      // message lists them — the draft is already saved, so nothing is lost.
      fail(err, t('templates.toast.publishFailed'));
    }
  };

  const duplicate = async (tpl: CheckTemplateSummary) => {
    if (!orgId) return;
    try {
      await duplicateMutation.mutateAsync({
        id: tpl.id,
        organisationId: orgId,
        name: t('templates.grid.copyName', { name: tpl.name }),
      });
      showToast({ type: 'success', message: t('templates.toast.duplicated') });
    } catch (err) {
      fail(err, t('templates.toast.duplicateFailed'));
    }
  };

  /**
   * Deleting is offered inside the editor, not on the grid — a bin icon on a
   * list row is too easy to hit by accident. The editor owns the confirm; this
   * only runs the mutation and returns to the grid.
   */
  const remove = async (id: string) => {
    if (!orgId) return;
    try {
      await deleteMutation.mutateAsync({ id, organisationId: orgId });
      showToast({ type: 'success', message: t('templates.toast.deleted') });
      setView({ kind: 'grid' });
    } catch (err) {
      // Rejected with a 409 once checks exist against the template.
      fail(err, t('templates.toast.deleteFailed'));
    }
  };

  // ── Sub-views ────────────────────────────────────────────────────────────

  if (!orgId) {
    return (
      <Shell>
        <ErrorBanner
          title={t('templates.noOrg.title')}
          message={t('templates.noOrg.message')}
        />
      </Shell>
    );
  }

  if (view.kind === 'new') {
    return (
      <Shell>
        <NewTemplateWizard
          isSaving={createMutation.isPending}
          onCancel={() => setView({ kind: 'grid' })}
          onBuild={(input) => void create({ ...input, organisationId: orgId })}
        />
      </Shell>
    );
  }

  if (view.kind === 'editor' || view.kind === 'preview') {
    if (detailQuery.isPending) {
      return (
        <Shell>
          <Loading label={t('templates.title')} />
        </Shell>
      );
    }
    if (detailQuery.isError || !detailQuery.data) {
      return (
        <Shell>
          <ErrorBanner
            title={t('templates.loadFailed')}
            message={detailQuery.error?.message ?? ''}
          />
        </Shell>
      );
    }

    if (view.kind === 'preview') {
      return (
        <Shell>
          <DriverPreview
            template={detailQuery.data}
            questions={view.questions}
            onBack={() => setView({ kind: 'editor', id: view.id })}
          />
        </Shell>
      );
    }

    return (
      <Shell>
        <TemplateEditor
          template={detailQuery.data}
          isSaving={isSaving}
          isDeleting={deleteMutation.isPending}
          onBack={() => setView({ kind: 'grid' })}
          onPreview={(questions) => setView({ kind: 'preview', id: view.id, questions })}
          onSaveDraft={(questions, name) => void saveDraft(questions, name)}
          onPublish={(questions, name) => void publish(questions, name)}
          onDelete={() => void remove(view.id)}
        />
      </Shell>
    );
  }

  // ── Grid ─────────────────────────────────────────────────────────────────

  return (
    <Shell>
      <div className="flex flex-wrap items-center gap-2 pb-3">
        <div className="flex-1" />
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="text"
            value={search}
            placeholder={t('templates.grid.search')}
            onChange={(e) => setSearch(e.target.value)}
            className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        <button
          type="button"
          onClick={() => setView({ kind: 'new' })}
          className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('templates.add')}
        </button>
      </div>

      {listQuery.isPending && <Loading label={t('templates.title')} />}

      {listQuery.isError && (
        <ErrorBanner title={t('templates.loadFailed')} message={listQuery.error.message} />
      )}

      {listQuery.isSuccess && (
        <div className="overflow-x-auto rounded-md">
          <div className="min-w-[760px]">
            <table className="w-full table-auto text-xs">
              <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <Th>{t('templates.grid.name')}</Th>
                  <Th>{t('templates.grid.unit')}</Th>
                  <Th>{t('templates.grid.regime')}</Th>
                  <Th align="center">{t('templates.grid.questions')}</Th>
                  <Th align="center">{t('templates.grid.status')}</Th>
                  <Th align="center">{t('templates.grid.version')}</Th>
                  <Th>{t('templates.grid.updated')}</Th>
                  <th className="sticky right-0 z-10 whitespace-nowrap bg-slate-700 px-3 py-2 text-right font-semibold">
                    {t('templates.grid.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-500 bg-white">
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-xs italic text-slate-400">
                      {search.trim() ? t('templates.grid.emptyFiltered') : t('templates.empty')}
                    </td>
                  </tr>
                ) : (
                  visible.map((tpl) => (
                    <tr
                      key={tpl.id}
                      className="group cursor-pointer hover:bg-slate-50"
                      title={t('templates.grid.doubleClickHint')}
                      onDoubleClick={() => setView({ kind: 'editor', id: tpl.id })}
                    >
                      <td className="whitespace-nowrap px-3 py-1.5 text-xs font-semibold text-slate-900">
                        {tpl.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-xs text-slate-600">
                        {unitLabel(tpl.unitType)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-xs text-slate-600">
                        {tpl.regime === 'Wac'
                          ? `${t('templates.regime.wacShort')} · ${t(
                              tpl.wacType === 'Type1' ? 'templates.wac.type1' : 'templates.wac.type2',
                            )}`
                          : t('templates.regime.preUseShort')}
                      </td>
                      <td className="px-3 py-1.5 text-center text-xs text-slate-600">
                        {tpl.questionCount}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[tpl.status]}`}
                        >
                          {t(
                            tpl.status === 'Published'
                              ? 'templates.status.published'
                              : tpl.status === 'Archived'
                                ? 'templates.status.archived'
                                : 'templates.status.draft',
                          )}
                        </span>
                        {/* Published, but with edits not yet live. */}
                        {tpl.hasUnpublishedDraft && tpl.status === 'Published' && (
                          <span className="ml-1 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                            {t('templates.status.draftPending')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-center text-xs text-slate-500">
                        v{tpl.version}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-[10px] text-slate-500">
                        {new Date(tpl.updatedUtc).toLocaleDateString()} · {tpl.updatedBy}
                      </td>
                      <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-3 py-1.5 text-right group-hover:bg-slate-50">
                        <RowAction
                          icon={Edit}
                          label={t('templates.grid.edit')}
                          onClick={() => setView({ kind: 'editor', id: tpl.id })}
                        />
                        <RowAction
                          icon={Copy}
                          label={t('templates.grid.duplicate')}
                          onClick={() => void duplicate(tpl)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Shell>
  );
}

/**
 * Shared page chrome. PageHeader takes no children so it emits its mb-9
 * spacer, matching every other screen in the suite.
 */
function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('checks');
  return (
    <div className="flex h-full min-h-0 flex-col px-2">
      <PageHeader title={t('templates.title')} description={t('templates.description')} />
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</main>
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'center' | 'right' }) {
  const cls = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  return <th className={`whitespace-nowrap px-3 py-2 font-semibold ${cls}`}>{children}</th>;
}

function RowAction({
  icon: Icon,
  label,
  tone,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  tone?: 'danger';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`ml-1 inline-flex h-5 w-5 items-center justify-center rounded-sm ${
        tone === 'danger' ? 'text-red-600 hover:bg-red-50' : 'text-blue-600 hover:bg-blue-50'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
